import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FC } from 'react';
import ProductItemRow from './ProductItemRow';
import { MarketItem } from '../../../app/services/api/orderManagementClient';
import { useTranslation } from 'react-i18next';
import { Pill } from '../../../components/shared/Pill';
import { TableLoading } from '../../../components/forms/TableLoading';
import { EmptyRows } from '../../../components/forms/EmptyRows';
import { EmptyTable } from '../../../components/shared/EmptyTable';

type Props = {
  list: MarketItem[];
  isLoading: boolean;
  totalCount: number;
  onClear: () => void;
  onCreateNew: () => void;
};

const ProductsList: FC<Props> = ({
  list,
  isLoading,
  totalCount,
  onClear,
  onCreateNew,
}) => {
  const { t } = useTranslation('productManagement');

  if (totalCount === 0 && !isLoading) {
    return (
      <EmptyTable
        bodyRowHeight={40}
        headerHeight={45}
        text={t('products')}
        searchTerm={''}
        onClear={onClear}
        onAdd={onCreateNew}
        onAddIsCreating={false}
        addItemText={t('createNew')}
        onAddIsDisabled={false}
        withPaginationShown={false}
      />
    );
  }

  return (
    <>
      <Box
        sx={{
          p: 5,
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        {/* Left: Title and Pill */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
          }}
        >
          <Typography
            variant="textMD"
            fontWeight={600}
          >
            {t('products')}
          </Typography>
          {(totalCount ?? 0) > 0 && <Pill text={`${totalCount} ${t('products')}`} />}
        </Box>
      </Box>
      
      {isLoading ? (
        <TableLoading
          headerHeight={45}
          bodyRowHeight={40}
          minTableWidth={1200}
        />
      ) : (
        <TableContainer>
          <Table
            sx={{
              borderBottomLeftRadius: '12px',
              borderBottomRightRadius: '12px',
              minWidth: '1200px',
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('itemNumber')}
                </TableCell>
                <TableCell
                  sx={{
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('itemName')}
                </TableCell>
                <TableCell
                  sx={{
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('category')}
                </TableCell>
                <TableCell
                  sx={{
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('primaryUom')}
                </TableCell>
                <TableCell
                  sx={{
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('orderableUom')}
                </TableCell>
                <TableCell
                  sx={{
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('packSize')}
                </TableCell>
                <TableCell
                  sx={{
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('caseSize')}
                </TableCell>
                <TableCell
                  sx={{
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('innerPackSize')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((row) => (
                <ProductItemRow
                  key={`${row.companyId}-${row.customerTypeId}-${row.itemNumber}`}
                  item={row}
                />
              ))}
              <EmptyRows
                numberOfCurrentRows={list.length}
                height={40}
              />
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default ProductsList;
